import { useEffect, useRef, useState } from 'react'
import { auroraStore, useAuroraStore } from '../../store/auroraStore'
import type { Interpretation } from '../../store/selectors'
import { skillLabels } from '../../store/selectors'
import { domainColor, domainLabel, frameworkDomains } from '../../lib/rubric'

/**
 * Correcting what the agent heard.
 *
 * A parent watched the thing happen; the model read a transcript. When they
 * disagree the parent is right, and saying so has to cost one tap and a few
 * seconds. Every edit here is also the best training signal the system will
 * ever get, which is why the store marks these captures as parent-edited.
 *
 * Fields the agent sent that this form does not show are carried through
 * untouched. Correcting a summary must never quietly delete something the
 * agent recorded and this screen has no opinion about.
 */

const LEVELS = [1, 2, 3, 4, 5] as const

export function EditExtraction({
  captureId,
  interpretation,
  transcript,
  onDone,
  onCancel,
}: {
  captureId: string
  interpretation: Interpretation | null
  transcript: string
  onDone: () => void
  onCancel: () => void
}) {
  const raw = useAuroraStore((s: any) => s.captures[captureId]?.interpretation)
  const labels = useAuroraStore(skillLabels)

  const [summary, setSummary] = useState(interpretation?.summary ?? '')
  const [domains, setDomains] = useState<string[]>(interpretation?.domains ?? [])
  const [quote, setQuote] = useState(interpretation?.quotes[0] ?? '')
  const [skills, setSkills] = useState(interpretation?.skills ?? [])

  const summaryRef = useRef<HTMLTextAreaElement>(null)
  useEffect(() => { summaryRef.current?.focus() }, [])

  const toggleDomain = (key: string) =>
    setDomains((prev) => (prev.includes(key) ? prev.filter((d) => d !== key) : [...prev, key]))

  const setLevel = (index: number, level: number) =>
    setSkills((prev) =>
      prev.map((sk, i) =>
        i === index ? { ...sk, suggestedLevel: sk.suggestedLevel === level ? undefined : level } : sk,
      ),
    )

  const save = () => {
    const rest = raw && typeof raw === 'object' ? raw : {}
    const quotes = quote.trim()
      ? [quote.trim(), ...(interpretation?.quotes ?? []).slice(1)]
      : (interpretation?.quotes ?? []).slice(1)

    ;(auroraStore.getState() as any).editInterpretation(captureId, {
      ...rest,
      summary: summary.trim(),
      domains,
      quotes,
      skills,
    })
    onDone()
  }

  // A domain the agent invented is offered too, so a correction can keep it.
  const known = frameworkDomains().map((d) => d.key as string)
  const ordered = [...domains.filter((d) => !known.includes(d)), ...known]

  return (
    <section
      className="extract extract-edit"
      role="dialog"
      aria-label="Correct this note"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel()
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) save()
      }}
    >
      <header className="extract-head">
        <span className="extract-label">Set it straight</span>
        <button className="extract-close" onClick={onCancel} aria-label="Back">
          <CloseIcon />
        </button>
      </header>

      {transcript && <p className="extract-said">{transcript}</p>}

      <label className="field">
        <span className="field-label">What happened</span>
        <textarea
          ref={summaryRef}
          className="field-input"
          rows={3}
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          placeholder="In your words. This is the version that gets kept."
        />
      </label>

      <fieldset className="field">
        <legend className="field-label">What it touched</legend>
        <div className="extract-picker">
          {ordered.map((key) => {
            const on = domains.includes(key)
            return (
              <button
                key={key}
                type="button"
                className={`extract-pick${on ? ' is-on' : ''}`}
                aria-pressed={on}
                style={{ '--domain-color': domainColor(key, 'var(--accent)') } as React.CSSProperties}
                onClick={() => toggleDomain(key)}
              >
                {domainLabel(key)}
              </button>
            )
          })}
        </div>
      </fieldset>

      {skills.length > 0 && (
        <fieldset className="field">
          <legend className="field-label">Where these sit now</legend>
          <div className="stack stack-2">
            {skills.map((skill, i) => (
              <div key={`${skill.name}:${i}`} className="extract-level-row">
                <span className="extract-level-name">{labels[skill.name] ?? skill.name}</span>
                <div className="extract-levels" role="group" aria-label={`Level for ${skill.name}`}>
                  {LEVELS.map((level) => (
                    <button
                      key={level}
                      type="button"
                      className={`extract-level${skill.suggestedLevel === level ? ' is-on' : ''}`}
                      aria-pressed={skill.suggestedLevel === level}
                      aria-label={`Level ${level}`}
                      onClick={() => setLevel(i, level)}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </fieldset>
      )}

      <label className="field">
        <span className="field-label">Something she said</span>
        <input
          className="field-input"
          value={quote}
          onChange={(e) => setQuote(e.target.value)}
          placeholder="Her exact words, if you caught them"
        />
      </label>

      <div className="extract-actions">
        <button className="btn btn-primary btn-sm" onClick={save}>
          Save my version
        </button>
        <button className="btn btn-quiet btn-sm" onClick={onCancel}>
          Back
        </button>
      </div>
    </section>
  )
}

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
)
